import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../utils/apiConfig';
import type { OrgFeatureFlagKey } from '../utils/orgFeatureFlagKeys';

/**
 * Pre-release flags resolved server-side for the caller's organization
 * (`GET /feature-flags/me`).
 *
 * The key list itself lives in `../utils/orgFeatureFlagKeys` so it can be
 * imported without React; it is re-exported here for existing callers.
 */
export { ORG_FEATURE_FLAG_KEYS } from '../utils/orgFeatureFlagKeys';
export type { OrgFeatureFlagKey } from '../utils/orgFeatureFlagKeys';

export interface ResolvedOrgFeatureFlags {
  organizationId: string | null;
  flags: Record<OrgFeatureFlagKey, boolean>;
  details: Array<{
    key: OrgFeatureFlagKey;
    label: string;
    enabled: boolean;
    source: 'default' | 'global' | 'organization' | 'plan';
  }>;
}

export const ORG_FEATURE_FLAGS_QUERY_KEY = ['org-feature-flags'] as const;

export const useOrgFeatureFlags = (options: { enabled?: boolean } = {}) => {
  const query = useQuery({
    // Off for callers that may have no session (a public page before sign-in).
    enabled: options.enabled ?? true,
    queryKey: [...ORG_FEATURE_FLAGS_QUERY_KEY, getImpersonationIdentityKey()],
    queryFn: async (): Promise<ResolvedOrgFeatureFlags | null> => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/feature-flags/me', { headers });
      if (!response.ok) return null;
      const body = await response.json();
      return body?.data ?? null;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const isEnabled = (key: OrgFeatureFlagKey): boolean => query.data?.flags?.[key] === true;

  /** Where the effective value came from. Undefined until the flags have been read. */
  const sourceOf = useCallback(
    (key: OrgFeatureFlagKey): ResolvedOrgFeatureFlags['details'][number]['source'] | undefined =>
      query.data?.details?.find((detail) => detail.key === key)?.source,
    [query.data],
  );

  /**
   * For a switch that is on unless an admin turned it off (a definition with
   * `defaultEnabled`, such as `ui_product_browser`): until the flags have been
   * read, or when they cannot be, it counts as on, so a slow or failed read
   * never hides a menu entry. A failed refetch keeps the earlier answer in
   * `data`, so `isError` is checked first: an older "off" must not stay in
   * force while the flags cannot be read.
   */
  const isOnUnlessOff = (key: OrgFeatureFlagKey): boolean => {
    if (query.isError) return true;
    const flags = query.data?.flags;
    return flags ? flags[key] !== false : true;
  };

  return { ...query, flags: query.data?.flags ?? null, isEnabled, isOnUnlessOff, sourceOf };
};

export const useOrgFeatureFlag = (key: OrgFeatureFlagKey): { enabled: boolean; isLoading: boolean } => {
  const { isEnabled, isLoading } = useOrgFeatureFlags();
  return { enabled: isEnabled(key), isLoading };
};
